import { Box, Checkbox } from "@mui/material";
import {
  Cancel as CancelIcon,
  AccessTime as ClockIcon,
  Person as PersonIcon,
  ArrowDropUp,
  ArrowDropDown,
} from "@mui/icons-material";
import { t } from "./coachTokens";
import ClientAvatar from "./ClientAvatar";
import FilterChip from "./FilterChip";

export const DAY_HEADERS = ["א", "ב", "ג", "ד", "ה", "ו", "ש"];

export const lastWorkoutLabel = (value) => {
  if (value == null) return { label: "מעולם לא", color: t.t3, weight: 500 };
  // Server returns Instant as epoch seconds (Jackson default with WRITE_DATES_AS_TIMESTAMPS=true).
  const d = typeof value === "number" ? new Date(value * 1000) : new Date(value);
  if (Number.isNaN(d.getTime())) return { label: "מעולם לא", color: t.t3, weight: 500 };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dayStart = new Date(d);
  dayStart.setHours(0, 0, 0, 0);
  const diff = Math.round((today - dayStart) / 86400000);
  if (diff <= 0) return { label: "היום", color: t.green, weight: 600 };
  if (diff === 1) return { label: "אתמול", color: t.green, weight: 600 };
  if (diff <= 7) return { label: `לפני ${diff} ימים`, color: t.text, weight: 600 };
  return { label: `לפני ${diff} ימים`, color: t.red, weight: 600 };
};

export const WorkoutsFilters = ({ filter, onChange }) => (
  <>
    <FilterChip
      active={filter === "none"}
      onClick={() => onChange(filter === "none" ? "all" : "none")}
      color={t.red}
      label={
        <>
          <CancelIcon sx={{ fontSize: 12 }} /> לא התאמנו השבוע
        </>
      }
    />
    <FilterChip
      active={filter === "incomplete"}
      onClick={() => onChange(filter === "incomplete" ? "all" : "incomplete")}
      color={t.t2}
      label={
        <>
          <ClockIcon sx={{ fontSize: 12 }} /> לא סיימו את היעד
        </>
      }
    />
  </>
);

const cell = { padding: "12px 14px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", verticalAlign: "middle", textAlign: "center" };
const thBase = {
  textAlign: "center",
  padding: "10px 14px",
  fontSize: "10.5px",
  fontWeight: 700,
  color: t.t3,
  borderBottom: `1px solid ${t.border}`,
  background: t.bg,
  whiteSpace: "nowrap",
  textTransform: "uppercase",
  letterSpacing: ".06em",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const SortableTh = ({ label, sortKey, currentKey, currentDir, onSortChange, align = "center", width }) => {
  const active = currentKey === sortKey;
  return (
    <Box
      component="th"
      onClick={() => onSortChange(sortKey)}
      sx={{
        ...thBase,
        textAlign: align,
        width,
        cursor: "pointer",
        userSelect: "none",
        color: active ? t.text : t.t3,
        "&:hover": { color: t.text },
      }}
    >
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: "2px", justifyContent: align === "center" ? "center" : "flex-start" }}>
        {label}
        <Box sx={{ display: "inline-flex", flexDirection: "column", lineHeight: 0.6, opacity: active ? 1 : 0.35 }}>
          <ArrowDropUp sx={{ fontSize: 14, color: active && currentDir === "ASC" ? t.green : t.t3, mb: "-4px" }} />
          <ArrowDropDown sx={{ fontSize: 14, color: active && currentDir === "DESC" ? t.green : t.t3, mt: "-4px" }} />
        </Box>
      </Box>
    </Box>
  );
};

const WorkoutsTab = ({ rows, selected, onToggleSelect, onToggleAll, sortKey, sortDir, onSortChange, onOpenProfile, onOpenActions }) => {
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.traineeId));

  if (rows.length === 0) {
    return (
      <Box sx={{ padding: "48px", textAlign: "center", color: t.t3, fontSize: "13px" }}>
        אין לקוחות להצגה
      </Box>
    );
  }

  return (
    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <Box component="th" sx={{ ...thBase, width: 34 }}>
            <Checkbox
              size="small"
              checked={allChecked}
              onChange={(e) => onToggleAll(rows, e.target.checked)}
              sx={{ p: 0 }}
            />
          </Box>
          <SortableTh align="start" label="שם לקוח" sortKey="name" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          {DAY_HEADERS.map((d, i) => (
            <SortableTh
              key={d}
              label={d}
              sortKey={`day-${i}`}
              currentKey={sortKey}
              currentDir={sortDir}
              onSortChange={onSortChange}
            />
          ))}
          <SortableTh label="סה״כ" sortKey="total" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="שבוע בבלוק" sortKey="blockWeek" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="אימון אחרון" sortKey="lastWorkout" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <Box component="th" sx={thBase}>פעולות</Box>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const total = (r.weekDays || []).filter(Boolean).length;
          const goal = r.workoutsGoal || 0;
          const metGoal = total >= goal && goal > 0;
          const last = lastWorkoutLabel(r.lastWorkoutDate);
          const isChecked = selected.has(r.traineeId);
          return (
            <Box
              component="tr"
              key={r.traineeId}
              onClick={() => onOpenProfile?.(r)}
              sx={{
                cursor: "pointer",
                transition: "background .1s",
                "&:hover td": { background: t.greenLight },
              }}
            >
              <Box component="td" sx={cell} onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  size="small"
                  checked={isChecked}
                  onChange={(e) => onToggleSelect(r.traineeId, e.target.checked)}
                  sx={{ p: 0 }}
                />
              </Box>
              <Box component="td" sx={{ ...cell, textAlign: "start" }}>
                <ClientAvatar name={r.traineeName} photoUrl={r.photoUrl} email={r.email} phone={r.phone} />
              </Box>
              {(r.weekDays || [null, null, null, null, null, null, null]).map((state, i) => {
                const done = state === "COMPLETED";
                const inProgress = state === "IN_PROGRESS";
                return (
                <Box component="td" key={i} sx={cell}>
                  <Box
                    title={inProgress ? "אימון בתהליך" : undefined}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: done ? t.greenLight : inProgress ? t.bg : t.bg,
                      color: done ? t.greenText : inProgress ? t.text : t.t3,
                      fontSize: "12px",
                      fontWeight: done || inProgress ? 700 : 400,
                    }}
                  >
                    {done ? "✓" : inProgress ? "⋯" : "–"}
                  </Box>
                </Box>
                );
              })}
              <Box component="td" sx={cell}>
                <Box component="span" dir="ltr" sx={{ display: "inline-block" }}>
                  <Box component="span" sx={{ fontWeight: 700, color: metGoal ? t.green : t.text }}>{total}</Box>
                  <Box component="span" sx={{ color: t.t2 }}> / {goal}</Box>
                </Box>
              </Box>
              <Box component="td" sx={cell}>
                <Box
                  component="span"
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: t.blueText,
                    background: t.blueLight,
                    padding: "2px 8px",
                    borderRadius: "20px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.blockWeek || 0}/{r.blockTotal || 0}
                </Box>
              </Box>
              <Box component="td" sx={cell}>
                <Box component="span" sx={{ fontSize: "12px", color: last.color, fontWeight: last.weight }}>
                  {last.label}
                </Box>
              </Box>
              <Box component="td" sx={cell} onClick={(e) => e.stopPropagation()}>
                <Box
                  component="button"
                  onClick={(e) => onOpenActions?.(e.currentTarget, r)}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "7px",
                    border: `1.5px solid ${t.border}`,
                    background: t.bg,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: t.t2,
                    "&:hover": { background: t.greenLight, borderColor: t.green, color: t.green },
                  }}
                >
                  <PersonIcon sx={{ fontSize: 14 }} />
                </Box>
              </Box>
            </Box>
          );
        })}
      </tbody>
    </Box>
  );
};

export default WorkoutsTab;
